import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckRounded, CloseRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { SERIF_DISPLAY } from "../Chat/ChatHero";
import { hapticSelection } from "../../lib/haptics";
import { overlaySurface, panelBorder } from "../Dashboard/overviewPanel";
import { headerIconButtonSx } from "../common/AdaptiveDialog";

interface FeatureLimitModalProps {
  open: boolean;
  /** Never offer a plan on native (a locked surface never mounts there: the paywall comes first). */
  canUpgrade: boolean;
  onClose: () => void;
  title: string;
  body?: string;
  /** Ticked lines above the trial button. */
  points: readonly string[];
  /** The `from` the trial page is opened with, e.g. "earnings". */
  from: string;
  /** Before it navigates: the caller's analytics. */
  onUpgrade?: () => void;
}

/** The upsell shell: a headline, ticked points and a free-trial button. */
export const FeatureLimitModal = ({
  open,
  canUpgrade,
  onClose,
  title,
  body,
  points,
  from,
  onUpgrade,
}: FeatureLimitModalProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const greenButtonSx = {
    backgroundColor: "#22c55e",
    color: "#ffffff",
    fontWeight: 600,
    textTransform: "none",
    height: 44,
    borderRadius: 2,
    "&:hover": { backgroundColor: "#16a34a" },
  } as const;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: isDark ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.25)",
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          maxWidth: 520,
          width: "100%",
          m: 2,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          backgroundColor: overlaySurface(isDark),
          backgroundImage: "none",
          border: `1px solid ${panelBorder(isDark)}`,
          boxShadow: "none",
          position: "relative",
        },
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        disableRipple
        sx={{
          ...headerIconButtonSx(isDark),
          position: "absolute",
          top: 12,
          right: 12,
        }}
      >
        <CloseRounded fontSize="small" />
      </IconButton>
      <Typography
        component="h2"
        sx={{
          fontFamily: SERIF_DISPLAY,
          fontWeight: 600,
          fontSize: { xs: "1.6rem", sm: "1.8rem" },
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
          mb: 1.5,
          pr: 4,
        }}
      >
        {title}
      </Typography>
      {body !== undefined && (
        <Typography
          sx={{
            fontSize: "0.9375rem",
            color: theme.palette.text.secondary,
            lineHeight: 1.55,
            mb: 2.5,
          }}
        >
          {body}
        </Typography>
      )}
      {canUpgrade && (
        <>
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              p: 0,
              m: 0,
              mb: 3,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {points.map((point) => (
              <Box
                component="li"
                key={point}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  fontSize: "0.875rem",
                }}
              >
                <CheckRounded
                  sx={{ fontSize: 18, color: "#22c55e", flex: "none" }}
                />
                {point}
              </Box>
            ))}
          </Box>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={() => {
              hapticSelection();
              onUpgrade?.();
              onClose();
              navigate(`/start-free-trial?from=${encodeURIComponent(from)}`);
            }}
            sx={greenButtonSx}
          >
            Start free trial
          </Button>
          <Button
            fullWidth
            onClick={onClose}
            sx={{
              mt: 1,
              minHeight: 44,
              textTransform: "none",
              borderRadius: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(0,0,0,0.03)",
              },
            }}
          >
            Not now
          </Button>
        </>
      )}
      {!canUpgrade && (
        <Button
          fullWidth
          variant="contained"
          disableElevation
          onClick={onClose}
          sx={greenButtonSx}
        >
          OK
        </Button>
      )}
    </Dialog>
  );
};
